import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  useMediaQuery,
  Checkbox,
  Alert,
  CircularProgress,
  Avatar,
  LinearProgress,
  Pagination,
  Stepper,
  Step,
  StepLabel,
  Fade,
  Grow,
  Slide,
  Radio,
  RadioGroup,
  FormControlLabel,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useParams } from 'react-router-dom';
import { getMothersByPartner } from '../api';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import PeopleIcon from '@mui/icons-material/People';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SchoolIcon from '@mui/icons-material/School';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FavoriteIcon from '@mui/icons-material/Favorite';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';

const MatchFunding = () => {
  const { partnerId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // State management
  const [activeTab, setActiveTab] = useState(0);
  const [mothers, setMothers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMothers, setSelectedMothers] = useState([]);
  const [poolAmount, setPoolAmount] = useState(100);
  const [targetedAmount, setTargetedAmount] = useState(30);

  
  // Step flow state
  const [currentStep, setCurrentStep] = useState(0);
  const [poolSteps] = useState(['Contribution Details', 'Impact Preview', 'Payment Method', 'Review & Confirm']);
  const [targetedSteps] = useState(['Select Mothers', 'Funding Type', 'Choose Amount', 'Payment Method', 'Review & Confirm']);

  
  // Pagination state
  const [page, setPage] = useState(1);
  const [mothersPerPage] = useState(6);
  
  // Payment method state
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [paymentMethods, setPaymentMethods] = useState([]);
  
  // Funding type state for targeted giving
  const [targetedFundingType, setTargetedFundingType] = useState('targeted'); // 'targeted' or 'schoolfees'
  const [fundingType, setFundingType] = useState('onetime'); // 'onetime' or 'recurring'
  const [recurringFrequency, setRecurringFrequency] = useState('monthly'); // 'monthly', 'quarterly', 'yearly'


  // Load data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const mothersData = await getMothersByPartner(partnerId);
        setMothers(mothersData.mothers || []);
        


        const savedPaymentMethods = localStorage.getItem(`paymentMethods_${partnerId}`);
        if (savedPaymentMethods) {
          setPaymentMethods(JSON.parse(savedPaymentMethods));
        } else {
          // Default payment methods if not saved
          setPaymentMethods([
            { id: 'card', name: 'Credit/Debit Card', icon: '💳' },
            { id: 'bank', name: 'Bank Transfer', icon: '🏦' },
            { id: 'mobile', name: 'Mobile Money', icon: '📱' },
            { id: 'crypto', name: 'Cryptocurrency', icon: '₿' }
          ]);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [partnerId]);

  // Load selected payment method from localStorage
  useEffect(() => {
    const savedSelectedMethod = localStorage.getItem(`selectedPaymentMethod_${partnerId}`);
    if (savedSelectedMethod) {
      setSelectedPaymentMethod(savedSelectedMethod);
    }
  }, [partnerId]);

  const calculateFundingGap = (mother) => {
    const annualEducationCost = 1200;
    const currentSavings = mother.savings || 0;
    const donorContributions = mother.donor_contributions || 0;
    const totalAvailable = currentSavings + donorContributions;
    return Math.max(0, annualEducationCost - totalAvailable);
  };

  const calculatePoolImpact = (amount) => {
    const mothersCount = mothers.length;
    const avgFundingGap = mothers.reduce((sum, mother) => sum + calculateFundingGap(mother), 0) / mothersCount;
    const mothersHelped = Math.floor(amount / (avgFundingGap / 12));
    const educationMonths = (amount / (avgFundingGap / 12)) * 12;
    
    return {
      mothersHelped: Math.min(mothersHelped, mothersCount),
      educationMonths: Math.round(educationMonths),
      avgFundingGap: Math.round(avgFundingGap),
    };
  };

  const handleMotherSelection = (motherId) => {
    setSelectedMothers(prev => 
      prev.includes(motherId) 
        ? prev.filter(id => id !== motherId)
        : [...prev, motherId]
    );
  };

  // Pagination calculation
  const indexOfLastMother = page * mothersPerPage;
  const indexOfFirstMother = indexOfLastMother - mothersPerPage;
  const currentMothers = mothers.slice(indexOfFirstMother, indexOfLastMother);
  const totalPages = Math.ceil(mothers.length / mothersPerPage);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // Step navigation
  const handleNextStep = () => {
    let currentSteps;
    if (activeTab === 0) currentSteps = poolSteps;
    else currentSteps = targetedSteps;
    
    if (currentStep < currentSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const resetToStep1 = () => {
    setCurrentStep(0);
    setSelectedMothers([]);
    setSelectedPaymentMethod('');
    setTargetedFundingType('targeted');
    setFundingType('onetime');
  };

  const handleTabChange = (e, newValue) => {
    setActiveTab(newValue);
    setCurrentStep(0);
    setSelectedMothers([]);
    setSelectedPaymentMethod('');
  };

  // Helper functions for payment methods
  const getPaymentIcon = (type, provider) => {
    if (type === 'card') {
      return '💳';
    } else if (type === 'mobile_money') {
      return '📱';
    }
    return '💳';
  };

  const getPaymentDisplayName = (type, provider) => {
    if (type === 'card') {
      return provider === 'visa' ? 'Visa Card' : 'Mastercard';
    } else if (type === 'mobile_money') {
      return provider === 'airtel' ? 'Airtel Money' : 'MTN Mobile Money';
    }
    return 'Payment Method';
  };

  const maskCardNumber = (number) => {
    if (number.length <= 4) return number;
    return '•'.repeat(number.length - 4) + number.slice(-4);
  };

  const maskPhoneNumber = (number) => {
    if (number.length <= 4) return number;
    return number.slice(0, 4) + '•'.repeat(number.length - 6) + number.slice(-2);
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: 'linear-gradient(135deg, #f92955 0%, #d41e3f 100%)'
      }}>
        <CircularProgress size={60} sx={{ color: '#fff' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: '#FFFFFF',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Animated Background Elements */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 20% 80%, rgba(249, 41, 85, 0.05) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(212, 30, 63, 0.05) 0%, transparent 50%)',
        animation: 'float 6s ease-in-out infinite',
        '@keyframes float': {
          '0%, 100%': { transform: 'translateY(0px)' },
          '50%': { transform: 'translateY(-20px)' }
        },
        '@keyframes shimmer': {
          '0%': { backgroundPosition: '-200% 0' },
          '100%': { backgroundPosition: '200% 0' }
        },
        '@keyframes pulse': {
          '0%, 100%': { transform: 'scale(1)', opacity: 0.3 },
          '50%': { transform: 'scale(1.05)', opacity: 0.5 }
        }
      }} />

      {/* Impact Cards Section */}
      <Box sx={{ 
        pt: 4,
        pb: 6,
        px: isMobile ? 1 : 2,
        position: 'relative',
        zIndex: 1
      }}>
        <Grow in timeout={1200}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'row',
            gap: 2,
            width: '100%',
            mb: 4,
            flexWrap: 'wrap'
          }}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 8px 32px rgba(249, 41, 85, 0.12)',
              background: 'linear-gradient(135deg, #f92955 0%, #d41e3f 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              flex: '1 1 250px',
              minWidth: '250px',
              maxWidth: '100%',
            }}>
              <Box sx={{
                position: 'absolute',
                top: -20,
                right: -20,
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)',
              }} />
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h4" sx={{ fontWeight: 800, fontSize: '2.5rem' }}>
                    {mothers.length}
                  </Typography>
                  <PeopleIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Total Mothers
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Available for funding
                </Typography>
              </CardContent>
            </Card>

            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 8px 32px rgba(187, 255, 193, 0.12)',
              background: 'linear-gradient(135deg, #BBFFC1 0%, #a8ffae 100%)',
              color: 'black',
              position: 'relative',
              overflow: 'hidden',
              flex: '1 1 250px',
              minWidth: '250px',
              maxWidth: '100%',
            }}>
              <Box sx={{
                position: 'absolute',
                top: -20,
                right: -20,
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'rgba(0,0,0,0.1)',
              }} />
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h4" sx={{ fontWeight: 800, fontSize: '2.5rem' }}>
                    ${mothers.reduce((sum, mother) => sum + (mother.savings || 0), 0).toLocaleString()}
                  </Typography>
                  <MonetizationOnIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Total Savings
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Accumulated by mothers
                </Typography>
              </CardContent>
            </Card>

            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 8px 32px rgba(249, 41, 85, 0.12)',
              background: 'linear-gradient(135deg, #f92955 0%, #d41e3f 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              flex: '1 1 250px',
              minWidth: '250px',
              maxWidth: '100%',
            }}>
              <Box sx={{
                position: 'absolute',
                top: -20,
                right: -20,
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)',
              }} />
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h4" sx={{ fontWeight: 800, fontSize: '2.5rem' }}>
                    ${mothers.reduce((sum, mother) => sum + (mother.donor_contributions || 0), 0).toLocaleString()}
                  </Typography>
                  <TrendingUpIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Total Contributions
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  From MatchFunders
                </Typography>
              </CardContent>
            </Card>

            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 8px 32px rgba(255,152,0,0.12)',
              background: 'linear-gradient(135deg, #E91E63 0%, #C2185B 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              flex: '1 1 250px',
              minWidth: '250px',
              maxWidth: '100%',
            }}>
              <Box sx={{
                position: 'absolute',
                top: -20,
                right: -20,
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)',
              }} />
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h4" sx={{ fontWeight: 800, fontSize: '2.5rem' }}>
                    {mothers.filter(m => m.donor_profile).length}
                  </Typography>
                  <EmojiEventsIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  With MatchFunders
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Receiving support
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Grow>
      </Box>

      {/* Main Content */}
      <Box sx={{ 
        px: isMobile ? 2 : 4, 
        pb: 6,
        position: 'relative',
        zIndex: 1
      }}>
        {/* Navigation Tabs */}
        <Paper sx={{ 
          mb: 4, 
          borderRadius: 4, 
          background: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          border: '1px solid rgba(255,255,255,0.2)'
        }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': {
                fontWeight: 700,
                fontSize: '1.1rem',
                py: 3,
                minHeight: 70,
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'rgba(249, 41, 85, 0.1)',
                  transform: 'translateY(-2px)'
                }
              },
              '& .Mui-selected': {
                color: '#f92955',
                transform: 'scale(1.05)'
              },
              '& .MuiTabs-indicator': {
                height: 4,
                borderRadius: 2,
                background: 'linear-gradient(45deg, #f92955, #d41e3f)'
              }
            }}
          >
            <Tab 
              icon={<PeopleIcon sx={{ fontSize: 28 }} />} 
              label="Pool Fund" 
              iconPosition="start"
            />
            <Tab 
              icon={<MonetizationOnIcon sx={{ fontSize: 28 }} />} 
              label="Targeted Giving" 
              iconPosition="start"
            />

          </Tabs>
        </Paper>

        {/* Tab Content */}
        <Slide direction="up" in timeout={500}>
          <Box>
            {/* Pool Fund Tab */}
            {activeTab === 0 && (
              <Grow in timeout={800}>
                <Card sx={{ 
                  borderRadius: 4,
                  background: 'rgba(255,255,255,0.95)',
                  backdropFilter: 'blur(20px)',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)'
                }}>
                  <Box sx={{
                    background: 'linear-gradient(135deg, #f92955 0%, #d41e3f 100%)',
                    p: 3,
                    color: '#fff'
                  }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                      🌟 Pool Fund Contribution
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.9 }}>
                      Step-by-step process to contribute to our collective fund
                    </Typography>
                  </Box>
                  
                  <CardContent sx={{ p: 4 }}>
                    {/* Enhanced Stepper */}
                    <Box sx={{ mb: 6 }}>
                      <Stepper 
                        activeStep={currentStep} 
                        alternativeLabel
                        sx={{ 
                          '& .MuiStepLabel-root .Mui-completed': {
                            color: '#f92955',
                          },
                          '& .MuiStepLabel-root .Mui-active': {
                            color: '#f92955',
                          },
                          '& .MuiStepIcon-root': {
                            fontSize: '2.5rem',
                            transition: 'all 0.3s ease',
                            '&.Mui-active': {
                              filter: 'drop-shadow(0 4px 8px rgba(249, 41, 85, 0.3))',
                            },
                            '&.Mui-completed': {
                              filter: 'drop-shadow(0 4px 8px rgba(249, 41, 85, 0.3))',
                            }
                          },
                          '& .MuiStepConnector-line': {
                            borderColor: '#e0e0e0',
                            borderTopWidth: 3,
                          },
                          '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line': {
                            borderColor: '#f92955',
                            borderTopWidth: 3,
                          },
                          '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': {
                            borderColor: '#f92955',
                            borderTopWidth: 3,
                          }
                        }}
                      >
                        {poolSteps.map((label, index) => (
                          <Step key={label}>
                            <StepLabel 
                              sx={{
                                '& .MuiStepLabel-label': {
                                  fontSize: '0.9rem',
                                  fontWeight: 600,
                                  color: currentStep >= index ? '#333' : '#999',
                                  transition: 'all 0.3s ease',
                                },
                                '& .MuiStepLabel-label.Mui-active': {
                                  color: '#f92955',
                                  fontWeight: 700,
                                },
                                '& .MuiStepLabel-label.Mui-completed': {
                                  color: '#f92955',
                                  fontWeight: 600,
                                }
                              }}
                            >
                              {label}
                            </StepLabel>
                          </Step>
                        ))}
                      </Stepper>
                      
                      {/* Progress Bar */}
                      <Box sx={{ mt: 3, mb: 2 }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={((currentStep + 1) / poolSteps.length) * 100}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: 'rgba(249, 41, 85, 0.1)',
                            '& .MuiLinearProgress-bar': {
                              background: 'linear-gradient(45deg, #f92955, #d41e3f)',
                              borderRadius: 4,
                              transition: 'transform 0.6s ease',
                            }
                          }}
                        />
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            textAlign: 'center', 
                            mt: 1, 
                            color: '#666',
                            fontWeight: 500
                          }}
                        >
                          Step {currentStep + 1} of {poolSteps.length}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Step Content with Enhanced Animations */}
                    <Fade in timeout={500}>
                      <Box>
                        {currentStep === 0 && (
                          <Grow in timeout={600}>
                            <Box>
                              <Box sx={{ 
                                textAlign: 'center', 
                                mb: 4,
                                p: 3,
                                background: 'linear-gradient(135deg, rgba(249, 41, 85, 0.05) 0%, rgba(212, 30, 63, 0.05) 100%)',
                                borderRadius: 3,
                                border: '1px solid rgba(249, 41, 85, 0.1)'
                              }}>
                                <Typography variant="h5" sx={{ 
                                  mb: 2, 
                                  fontWeight: 700, 
                                  color: '#333',
                                  background: 'linear-gradient(135deg, #f92955, #d41e3f)',
                                  backgroundClip: 'text',
                                  WebkitBackgroundClip: 'text',
                                  WebkitTextFillColor: 'transparent',
                                }}>
                                  💰 Contribution Details
                                </Typography>
                                <Typography variant="body1" sx={{ color: '#666', maxWidth: 600, mx: 'auto' }}>
                                  Set your contribution amount and frequency to help mothers achieve their education goals
                                </Typography>
                              </Box>
                              
                              <Grid container spacing={4}>
                                <Grid item xs={12} md={6}>
                                  <Box sx={{ position: 'relative' }}>
                                    <TextField
                                      fullWidth
                                      label="Contribution Amount (USD)"
                                      type="number"
                                      value={poolAmount}
                                      onChange={(e) => setPoolAmount(Number(e.target.value))}
                                      sx={{
                                        '& .MuiOutlinedInput-root': {
                                          borderRadius: 3,
                                          fontSize: '1.1rem',
                                          fontWeight: 600,
                                          '&:hover fieldset': {
                                            borderColor: '#f92955',
                                            borderWidth: 2,
                                          },
                                          '&.Mui-focused fieldset': {
                                            borderColor: '#f92955',
                                            borderWidth: 2,
                                          },
                                          '&.Mui-focused': {
                                            transform: 'scale(1.02)',
                                            transition: 'transform 0.2s ease',
                                          }
                                        },
                                        '& .MuiInputLabel-root': {
                                          color: '#666',
                                          fontWeight: 600,
                                          '&.Mui-focused': {
                                            color: '#f92955',
                                          }
                                        }
                                      }}
                                      InputProps={{
                                        startAdornment: (
                                          <Typography sx={{ 
                                            mr: 1, 
                                            color: '#f92955', 
                                            fontWeight: 700,
                                            fontSize: '1.2rem'
                                          }}>
                                            $
                                          </Typography>
                                        ),
                                      }}
                                    />
                                    <Typography variant="caption" sx={{ 
                                      color: '#999', 
                                      mt: 1, 
                                      display: 'block',
                                      fontStyle: 'italic'
                                    }}>
                                      Recommended: $30 - $100 per month
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <FormControl fullWidth>
                                    <InputLabel sx={{ 
                                      color: '#666', 
                                      fontWeight: 600,
                                      '&.Mui-focused': {
                                        color: '#f92955',
                                      }
                                    }}>
                                      Contribution Frequency
                                    </InputLabel>
                                    <Select
                                      value="monthly"
                                      label="Contribution Frequency"
                                      sx={{
                                        borderRadius: 3,
                                        fontSize: '1.1rem',
                                        fontWeight: 600,
                                        '& .MuiOutlinedInput-notchedOutline': {
                                          borderColor: '#e0e0e0',
                                          borderWidth: 2,
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                          borderColor: '#f92955',
                                          borderWidth: 2,
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                          borderColor: '#f92955',
                                          borderWidth: 2,
                                        },
                                        '&.Mui-focused': {
                                          transform: 'scale(1.02)',
                                          transition: 'transform 0.2s ease',
                                        }
                                      }}
                                    >
                                      <MenuItem value="monthly" sx={{ fontWeight: 600 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <span>📅</span>
                                          <span>Monthly</span>
                                        </Box>
                                      </MenuItem>
                                      <MenuItem value="quarterly" sx={{ fontWeight: 600 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <span>📊</span>
                                          <span>Quarterly</span>
                                        </Box>
                                      </MenuItem>
                                      <MenuItem value="one-time" sx={{ fontWeight: 600 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <span>💎</span>
                                          <span>One Time</span>
                                        </Box>
                                      </MenuItem>
                                    </Select>
                                  </FormControl>
                                </Grid>
                              </Grid>

                              <Box sx={{ 
                                mt: 6, 
                                display: 'flex', 
                                justifyContent: 'flex-end',
                                gap: 2
                              }}>
                                <Button
                                  variant="contained"
                                  endIcon={<NavigateNextIcon />}
                                  onClick={handleNextStep}
                                  sx={{
                                    background: 'linear-gradient(45deg, #f92955, #d41e3f)',
                                    borderRadius: 3,
                                    px: 4,
                                    py: 1.5,
                                    fontSize: '1rem',
                                    fontWeight: 700,
                                    textTransform: 'none',
                                    boxShadow: '0 8px 25px rgba(249, 41, 85, 0.3)',
                                    '&:hover': {
                                      background: 'linear-gradient(45deg, #d41e3f, #b01a35)',
                                      transform: 'translateY(-2px)',
                                      boxShadow: '0 12px 35px rgba(249, 41, 85, 0.4)',
                                    },
                                    transition: 'all 0.3s ease',
                                  }}
                                >
                                  Continue to Preview
                                </Button>
                              </Box>
                            </Box>
                          </Grow>
                        )}

                        {currentStep === 1 && (
                          <Grow in timeout={600}>
                            <Box>
                              <Box sx={{ 
                                textAlign: 'center', 
                                mb: 4,
                                p: 3,
                                background: 'linear-gradient(135deg, rgba(249, 41, 85, 0.05) 0%, rgba(212, 30, 63, 0.05) 100%)',
                                borderRadius: 3,
                                border: '1px solid rgba(249, 41, 85, 0.1)'
                              }}>
                                <Typography variant="h5" sx={{ 
                                  mb: 2, 
                                  fontWeight: 700, 
                                  color: '#333',
                                  background: 'linear-gradient(135deg, #f92955, #d41e3f)',
                                  backgroundClip: 'text',
                                  WebkitBackgroundClip: 'text',
                                  WebkitTextFillColor: 'transparent',
                                }}>
                                  📊 Impact Preview
                                </Typography>
                                <Typography variant="body1" sx={{ color: '#666', maxWidth: 600, mx: 'auto' }}>
                                  See the real impact your contribution will make in mothers' lives
                                </Typography>
                              </Box>
                              
                              {(() => {
                                const impact = calculatePoolImpact(poolAmount);
                                return (
                                  <Grid container spacing={4}>
                                    <Grid item xs={12} md={6}>
                                      <Card sx={{ 
                                        background: 'linear-gradient(135deg, #f92955 0%, #d41e3f 100%)',
                                        color: '#fff',
                                        borderRadius: 4,
                                        boxShadow: '0 15px 35px rgba(249, 41, 85, 0.3)',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                          transform: 'translateY(-8px)',
                                          boxShadow: '0 20px 40px rgba(249, 41, 85, 0.4)',
                                        }
                                      }}>
                                        <CardContent sx={{ textAlign: 'center', py: 5 }}>
                                          <Typography variant="h1" sx={{ 
                                            fontWeight: 900, 
                                            mb: 2,
                                            fontSize: '4rem',
                                            textShadow: '0 4px 8px rgba(0,0,0,0.2)'
                                          }}>
                                            {impact.mothersHelped}
                                          </Typography>
                                          <Typography variant="h5" sx={{ 
                                            opacity: 0.9,
                                            fontWeight: 600,
                                            mb: 1
                                          }}>
                                            Mothers Will Be Helped
                                          </Typography>
                                          <Typography variant="body1" sx={{ opacity: 0.8 }}>
                                            Direct impact on education funding
                                          </Typography>
                                        </CardContent>
                                      </Card>
                                    </Grid>
                                    
                                    <Grid item xs={12} md={6}>
                                      <Card sx={{ 
                                        background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
                                        color: '#fff',
                                        borderRadius: 4,
                                        boxShadow: '0 15px 35px rgba(76, 175, 80, 0.3)',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                          transform: 'translateY(-8px)',
                                          boxShadow: '0 20px 40px rgba(76, 175, 80, 0.4)',
                                        }
                                      }}>
                                        <CardContent sx={{ textAlign: 'center', py: 5 }}>
                                          <Typography variant="h1" sx={{ 
                                            fontWeight: 900, 
                                            mb: 2,
                                            fontSize: '4rem',
                                            textShadow: '0 4px 8px rgba(0,0,0,0.2)'
                                          }}>
                                            {impact.educationMonths}
                                          </Typography>
                                          <Typography variant="h5" sx={{ 
                                            opacity: 0.9,
                                            fontWeight: 600,
                                            mb: 1
                                          }}>
                                            Months of Education
                                          </Typography>
                                          <Typography variant="body1" sx={{ opacity: 0.8 }}>
                                            Quality education provided
                                          </Typography>
                                        </CardContent>
                                      </Card>
                                    </Grid>
                                    
                                    <Grid item xs={12}>
                                      <Card sx={{ 
                                        background: 'linear-gradient(135deg, rgba(249, 41, 85, 0.1) 0%, rgba(212, 30, 63, 0.1) 100%)',
                                        border: '1px solid rgba(249, 41, 85, 0.2)',
                                        borderRadius: 3
                                      }}>
                                        <CardContent sx={{ textAlign: 'center', py: 3 }}>
                                          <Typography variant="body1" sx={{ 
                                            color: '#666', 
                                            fontStyle: 'italic',
                                            fontWeight: 500
                                          }}>
                                            💡 Based on average funding gap of <strong>${impact.avgFundingGap}</strong> per mother
                                          </Typography>
                                          <Typography variant="body2" sx={{ 
                                            color: '#999', 
                                            mt: 1,
                                            fontSize: '0.9rem'
                                          }}>
                                            Your contribution will be distributed to mothers with the greatest need
                                          </Typography>
                                        </CardContent>
                                      </Card>
                                    </Grid>
                                  </Grid>
                                );
                              })()}

                              <Box sx={{ 
                                mt: 6, 
                                display: 'flex', 
                                justifyContent: 'space-between',
                                gap: 2
                              }}>
                                <Button
                                  variant="outlined"
                                  startIcon={<NavigateBeforeIcon />}
                                  onClick={handlePrevStep}
                                  sx={{
                                    borderColor: '#f92955',
                                    color: '#f92955',
                                    borderRadius: 3,
                                    px: 4,
                                    py: 1.5,
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    '&:hover': {
                                      borderColor: '#d41e3f',
                                      backgroundColor: 'rgba(249, 41, 85, 0.1)',
                                      transform: 'translateY(-2px)',
                                    },
                                    transition: 'all 0.3s ease',
                                  }}
                                >
                                  Back to Details
                                </Button>
                                <Button
                                  variant="contained"
                                  endIcon={<NavigateNextIcon />}
                                  onClick={handleNextStep}
                                  sx={{
                                    background: 'linear-gradient(45deg, #f92955, #d41e3f)',
                                    borderRadius: 3,
                                    px: 4,
                                    py: 1.5,
                                    fontSize: '1rem',
                                    fontWeight: 700,
                                    textTransform: 'none',
                                    boxShadow: '0 8px 25px rgba(249, 41, 85, 0.3)',
                                    '&:hover': {
                                      background: 'linear-gradient(45deg, #d41e3f, #b01a35)',
                                      transform: 'translateY(-2px)',
                                      boxShadow: '0 12px 35px rgba(249, 41, 85, 0.4)',
                                    },
                                    transition: 'all 0.3s ease',
                                  }}
                                >
                                  Choose Payment Method
                                </Button>
                              </Box>
                            </Box>
                          </Grow>
                        )}

                        {currentStep === 2 && (
                          <Grow in timeout={600}>
                            <Box>
                              <Box sx={{ 
                                textAlign: 'center', 
                                mb: 4,
                                p: 3,
                                background: 'linear-gradient(135deg, rgba(249, 41, 85, 0.05) 0%, rgba(212, 30, 63, 0.05) 100%)',
                                borderRadius: 3,
                                border: '1px solid rgba(249, 41, 85, 0.1)'
                              }}>
                                <Typography variant="h5" sx={{ 
                                  mb: 2, 
                                  fontWeight: 700, 
                                  color: '#333',
                                  background: 'linear-gradient(135deg, #f92955, #d41e3f)',
                                  backgroundClip: 'text',
                                  WebkitBackgroundClip: 'text',
                                  WebkitTextFillColor: 'transparent',
                                }}>
                                  💳 Payment Method
                                </Typography>
                                <Typography variant="body1" sx={{ color: '#666', maxWidth: 600, mx: 'auto' }}>
                                  Select your preferred payment method for secure transactions
                                </Typography>
                              </Box>
                              
                              {paymentMethods.length === 0 ? (
                                <Card sx={{ 
                                  textAlign: 'center', 
                                  py: 6,
                                  background: 'linear-gradient(135deg, rgba(249, 41, 85, 0.05) 0%, rgba(212, 30, 63, 0.05) 100%)',
                                  border: '2px dashed rgba(249, 41, 85, 0.3)',
                                  borderRadius: 3
                                }}>
                                  <Typography variant="h4" sx={{ color: '#999', mb: 2 }}>
                                    🚫
                                  </Typography>
                                  <Typography variant="h6" sx={{ color: '#999', mb: 1 }}>
                                    No Payment Methods Available
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: '#999', mb: 4, maxWidth: 400, mx: 'auto' }}>
                                    Please add payment methods in Settings to continue with your contribution
                                  </Typography>
                                  <Button
                                    variant="outlined"
                                    onClick={() => window.location.href = `/partner/${partnerId}/settings`}
                                    sx={{
                                      borderColor: '#f92955',
                                      color: '#f92955',
                                      borderRadius: 3,
                                      px: 4,
                                      py: 1.5,
                                      fontSize: '1rem',
                                      fontWeight: 600,
                                      textTransform: 'none',
                                      '&:hover': {
                                        borderColor: '#d41e3f',
                                        bgcolor: '#f9295515',
                                        transform: 'translateY(-2px)',
                                      },
                                      transition: 'all 0.3s ease',
                                    }}
                                  >
                                    Go to Settings
                                  </Button>
                                </Card>
                              ) : (
                                <Grid container spacing={3}>
                                  {paymentMethods.map((method) => (
                                    <Grid item xs={12} sm={6} md={4} key={method.id}>
                                      <Card 
                                        sx={{ 
                                          cursor: 'pointer',
                                          border: selectedPaymentMethod === method.id ? '3px solid #f92955' : '2px solid #e0e0e0',
                                          borderRadius: 4,
                                          transition: 'all 0.3s ease',
                                          background: selectedPaymentMethod === method.id 
                                            ? 'linear-gradient(135deg, rgba(249, 41, 85, 0.1) 0%, rgba(212, 30, 63, 0.1) 100%)'
                                            : 'rgba(255,255,255,0.9)',
                                          '&:hover': { 
                                            transform: 'translateY(-8px) scale(1.02)',
                                            boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                                            borderColor: '#f92955',
                                          },
                                          '&.selected': {
                                            borderColor: '#f92955',
                                            background: 'linear-gradient(135deg, rgba(249, 41, 85, 0.1) 0%, rgba(212, 30, 63, 0.1) 100%)',
                                          }
                                        }}
                                        onClick={() => setSelectedPaymentMethod(method.id)}
                                        className={selectedPaymentMethod === method.id ? 'selected' : ''}
                                      >
                                        <CardContent sx={{ textAlign: 'center', p: 4 }}>
                                          <Box sx={{
                                            width: 60,
                                            height: 60,
                                            borderRadius: '50%',
                                            background: selectedPaymentMethod === method.id 
                                              ? 'linear-gradient(135deg, #f92955, #d41e3f)'
                                              : 'rgba(249, 41, 85, 0.1)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mx: 'auto',
                                            mb: 3,
                                            transition: 'all 0.3s ease',
                                          }}>
                                            <Typography variant="h3" sx={{ 
                                              fontSize: '2rem',
                                              color: selectedPaymentMethod === method.id ? '#fff' : '#f92955'
                                            }}>
                                              {getPaymentIcon(method.type, method.provider)}
                                            </Typography>
                                          </Box>
                                          <Typography variant="h6" sx={{ 
                                            fontWeight: 700, 
                                            color: '#333', 
                                            mb: 1 
                                          }}>
                                            {getPaymentDisplayName(method.type, method.provider)}
                                          </Typography>
                                          <Typography variant="body2" sx={{ 
                                            color: '#666', 
                                            fontSize: '0.9rem',
                                            mb: 2
                                          }}>
                                            {method.name}
                                          </Typography>
                                          <Typography variant="body2" sx={{ 
                                            color: '#999', 
                                            fontSize: '0.8rem',
                                            fontFamily: 'monospace',
                                            background: 'rgba(0,0,0,0.05)',
                                            p: 1,
                                            borderRadius: 2
                                          }}>
                                            {method.type === 'card' 
                                              ? maskCardNumber(method.number)
                                              : maskPhoneNumber(method.number)
                                            }
                                          </Typography>
                                          {selectedPaymentMethod === method.id && (
                                            <Box sx={{
                                              position: 'absolute',
                                              top: 12,
                                              right: 12,
                                              width: 24,
                                              height: 24,
                                              borderRadius: '50%',
                                              background: '#f92955',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              color: '#fff',
                                              fontSize: '0.8rem',
                                              fontWeight: 'bold'
                                            }}>
                                              ✓
                                            </Box>
                                          )}
                                        </CardContent>
                                      </Card>
                                    </Grid>
                                  ))}
                                </Grid>
                              )}

                              <Box sx={{ 
                                mt: 6, 
                                display: 'flex', 
                                justifyContent: 'space-between',
                                gap: 2
                              }}>
                                <Button
                                  variant="outlined"
                                  startIcon={<NavigateBeforeIcon />}
                                  onClick={handlePrevStep}
                                  sx={{
                                    borderColor: '#f92955',
                                    color: '#f92955',
                                    borderRadius: 3,
                                    px: 4,
                                    py: 1.5,
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    '&:hover': {
                                      borderColor: '#d41e3f',
                                      backgroundColor: 'rgba(249, 41, 85, 0.1)',
                                      transform: 'translateY(-2px)',
                                    },
                                    transition: 'all 0.3s ease',
                                  }}
                                >
                                  Back to Preview
                                </Button>
                                <Button
                                  variant="contained"
                                  endIcon={<NavigateNextIcon />}
                                  onClick={handleNextStep}
                                  disabled={!selectedPaymentMethod}
                                  sx={{
                                    background: 'linear-gradient(45deg, #f92955, #d41e3f)',
                                    borderRadius: 3,
                                    px: 4,
                                    py: 1.5,
                                    fontSize: '1rem',
                                    fontWeight: 700,
                                    textTransform: 'none',
                                    boxShadow: '0 8px 25px rgba(249, 41, 85, 0.3)',
                                    '&:hover': {
                                      background: 'linear-gradient(45deg, #d41e3f, #b01a35)',
                                      transform: 'translateY(-2px)',
                                      boxShadow: '0 12px 35px rgba(249, 41, 85, 0.4)',
                                    },
                                    '&:disabled': {
                                      background: '#ccc',
                                      transform: 'none',
                                      boxShadow: 'none',
                                    },
                                    transition: 'all 0.3s ease',
                                  }}
                                >
                                  Review & Confirm
                                </Button>
                              </Box>
                            </Box>
                          </Grow>
                        )}

                        {currentStep === 3 && (
                          <Grow in timeout={600}>
                            <Box>
                              <Box sx={{ 
                                textAlign: 'center', 
                                mb: 4,
                                p: 3,
                                background: 'linear-gradient(135deg, rgba(249, 41, 85, 0.05) 0%, rgba(212, 30, 63, 0.05) 100%)',
                                borderRadius: 3,
                                border: '1px solid rgba(249, 41, 85, 0.1)'
                              }}>
                                <Typography variant="h5" sx={{ 
                                  mb: 2, 
                                  fontWeight: 700, 
                                  color: '#333',
                                  background: 'linear-gradient(135deg, #f92955, #d41e3f)',
                                  backgroundClip: 'text',
                                  WebkitBackgroundClip: 'text',
                                  WebkitTextFillColor: 'transparent',
                                }}>
                                  ✅ Review & Confirm
                                </Typography>
                                <Typography variant="body1" sx={{ color: '#666', maxWidth: 600, mx: 'auto' }}>
                                  Review your contribution details before final submission
                                </Typography>
                              </Box>
                              
                              <Grid container spacing={4}>
                                <Grid item xs={12} md={8}>
                                  <Card sx={{ 
                                    background: 'linear-gradient(135deg, rgba(249, 41, 85, 0.05) 0%, rgba(212, 30, 63, 0.05) 100%)',
                                    border: '2px solid rgba(249, 41, 85, 0.2)',
                                    borderRadius: 4,
                                    boxShadow: '0 10px 30px rgba(249, 41, 85, 0.1)'
                                  }}>
                                    <CardContent sx={{ p: 4 }}>
                                      <Typography variant="h6" sx={{ 
                                        mb: 3, 
                                        color: '#333', 
                                        fontWeight: 700,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1
                                      }}>
                                        📋 Contribution Summary
                                      </Typography>
                                      
                                      <Box sx={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        alignItems: 'center',
                                        mb: 3,
                                        p: 2,
                                        background: 'rgba(255,255,255,0.7)',
                                        borderRadius: 2
                                      }}>
                                        <Typography variant="body1" sx={{ color: '#666', fontWeight: 600 }}>
                                          Contribution Amount:
                                        </Typography>
                                        <Typography variant="h5" sx={{ 
                                          fontWeight: 700, 
                                          color: '#333',
                                          background: 'linear-gradient(135deg, #f92955, #d41e3f)',
                                          backgroundClip: 'text',
                                          WebkitBackgroundClip: 'text',
                                          WebkitTextFillColor: 'transparent',
                                        }}>
                                          ${poolAmount}
                                        </Typography>
                                      </Box>
                                      
                                      <Box sx={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        alignItems: 'center',
                                        mb: 3,
                                        p: 2,
                                        background: 'rgba(255,255,255,0.7)',
                                        borderRadius: 2
                                      }}>
                                        <Typography variant="body1" sx={{ color: '#666', fontWeight: 600 }}>
                                          Frequency:
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#333' }}>
                                          Monthly
                                        </Typography>
                                      </Box>
                                      
                                      <Box sx={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        alignItems: 'center',
                                        mb: 3,
                                        p: 2,
                                        background: 'rgba(255,255,255,0.7)',
                                        borderRadius: 2
                                      }}>
                                        <Typography variant="body1" sx={{ color: '#666', fontWeight: 600 }}>
                                          Payment Method:
                                        </Typography>
                                        <Box sx={{ textAlign: 'right' }}>
                                          <Typography variant="body1" sx={{ fontWeight: 600, color: '#333' }}>
                                            {(() => {
                                              const method = paymentMethods.find(m => m.id === selectedPaymentMethod);
                                              return method ? getPaymentDisplayName(method.type, method.provider) : 'Not selected';
                                            })()}
                                          </Typography>
                                          {(() => {
                                            const method = paymentMethods.find(m => m.id === selectedPaymentMethod);
                                            return method && (
                                              <Typography variant="body2" sx={{ 
                                                color: '#999', 
                                                fontSize: '0.8rem',
                                                fontFamily: 'monospace'
                                              }}>
                                                {method.name} • {method.type === 'card' 
                                                  ? maskCardNumber(method.number)
                                                  : maskPhoneNumber(method.number)
                                                }
                                              </Typography>
                                            );
                                          })()}
                                        </Box>
                                      </Box>
                                    </CardContent>
                                  </Card>
                                </Grid>
                                
                                <Grid item xs={12} md={4}>
                                  {(() => {
                                    const impact = calculatePoolImpact(poolAmount);
                                    return (
                                      <Card sx={{ 
                                        background: 'linear-gradient(135deg, #f92955 0%, #d41e3f 100%)',
                                        color: '#fff',
                                        borderRadius: 4,
                                        boxShadow: '0 15px 35px rgba(249, 41, 85, 0.3)',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center'
                                      }}>
                                        <CardContent sx={{ textAlign: 'center', py: 4 }}>
                                          <Typography variant="h2" sx={{ 
                                            fontWeight: 900, 
                                            mb: 2,
                                            fontSize: '3.5rem',
                                            textShadow: '0 4px 8px rgba(0,0,0,0.2)'
                                          }}>
                                            {impact.mothersHelped}
                                          </Typography>
                                          <Typography variant="h6" sx={{ 
                                            opacity: 0.9, 
                                            mb: 2,
                                            fontWeight: 600
                                          }}>
                                            Mothers Will Be Helped
                                          </Typography>
                                          <Typography variant="body2" sx={{ opacity: 0.8, mb: 2 }}>
                                            {impact.educationMonths} months of education
                                          </Typography>
                                          <Box sx={{
                                            width: '100%',
                                            height: 4,
                                            background: 'rgba(255,255,255,0.3)',
                                            borderRadius: 2,
                                            overflow: 'hidden'
                                          }}>
                                            <Box sx={{
                                              width: '100%',
                                              height: '100%',
                                              background: 'rgba(255,255,255,0.8)',
                                              borderRadius: 2,
                                              animation: 'pulse 2s ease-in-out infinite'
                                            }} />
                                          </Box>
                                        </CardContent>
                                      </Card>
                                    );
                                  })()}
                                </Grid>
                              </Grid>

                              <Box sx={{ 
                                mt: 6, 
                                display: 'flex', 
                                justifyContent: 'space-between',
                                gap: 2
                              }}>
                                <Button
                                  variant="outlined"
                                  startIcon={<NavigateBeforeIcon />}
                                  onClick={handlePrevStep}
                                  sx={{
                                    borderColor: '#f92955',
                                    color: '#f92955',
                                    borderRadius: 3,
                                    px: 4,
                                    py: 1.5,
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    '&:hover': {
                                      borderColor: '#d41e3f',
                                      backgroundColor: 'rgba(249, 41, 85, 0.1)',
                                      transform: 'translateY(-2px)',
                                    },
                                    transition: 'all 0.3s ease',
                                  }}
                                >
                                  Back to Payment
                                </Button>
                                <Button
                                  variant="contained"
                                  startIcon={<FavoriteIcon />}
                                  onClick={() => {
                                    alert('Pool fund contribution submitted successfully!');
                                    resetToStep1();
                                  }}
                                  sx={{
                                    background: 'linear-gradient(45deg, #f92955, #d41e3f)',
                                    borderRadius: 3,
                                    px: 4,
                                    py: 1.5,
                                    fontSize: '1rem',
                                    fontWeight: 700,
                                    textTransform: 'none',
                                    boxShadow: '0 8px 25px rgba(249, 41, 85, 0.3)',
                                    '&:hover': {
                                      background: 'linear-gradient(45deg, #d41e3f, #b01a35)',
                                      transform: 'translateY(-2px)',
                                      boxShadow: '0 12px 35px rgba(249, 41, 85, 0.4)',
                                    },
                                    transition: 'all 0.3s ease',
                                  }}
                                >
                                  Confirm & Submit
                                </Button>
                              </Box>
                            </Box>
                          </Grow>
                        )}
                      </Box>
                    </Fade>
                  </CardContent>
                </Card>
              </Grow>
            )}

            {/* Targeted Giving Tab */}
            {activeTab === 1 && (
              <Grow in timeout={800}>
                <Card sx={{ 
                  borderRadius: 4,
                  background: 'rgba(255,255,255,0.95)',
                  backdropFilter: 'blur(20px)',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)'
                }}>
                  <Box sx={{
                    background: 'linear-gradient(135deg, #f92955 0%, #d41e3f 100%)',
                    p: 3,
                    color: '#fff'
                  }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                      💝 Targeted Giving
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.9 }}>
                      Step-by-step process to support specific mothers
                    </Typography>
                  </Box>
                  
                  <CardContent sx={{ p: 4 }}>
                                         {/* Stepper */}
                     <Box sx={{ mb: 4 }}>
                       <Stepper activeStep={currentStep} sx={{ 
                         '& .MuiStepLabel-root .Mui-completed': {
                           color: '#f92955',
                         },
                         '& .MuiStepLabel-root .Mui-active': {
                           color: '#f92955',
                         },
                         '& .MuiStepIcon-root': {
                           fontSize: '2rem',
                         }
                       }}>
                         {targetedSteps.map((label) => (
                           <Step key={label}>
                             <StepLabel>{label}</StepLabel>
                           </Step>
                         ))}
                       </Stepper>
                     </Box>

                    {/* Step Content */}
                    {currentStep === 0 && (
                      <Box>
                        <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#333' }}>
                          Select Mothers ({selectedMothers.length} selected)
                        </Typography>
                        
                        <Grid container spacing={2}>
                          {currentMothers.map((mother) => (
                            <Grid item xs={12} md={6} key={mother.generated_id}>
                              <Card 
                                sx={{ 
                                  cursor: 'pointer',
                                  border: selectedMothers.includes(mother.generated_id) ? '3px solid #f92955' : '1px solid #e0e0e0',
                                  borderRadius: 3,
                                  transition: 'all 0.3s ease',
                                  '&:hover': { 
                                    transform: 'translateY(-4px)',
                                    boxShadow: '0 12px 30px rgba(0,0,0,0.15)'
                                  },
                                  background: selectedMothers.includes(mother.generated_id) 
                                    ? 'linear-gradient(135deg, rgba(249, 41, 85, 0.1) 0%, rgba(212, 30, 63, 0.1) 100%)'
                                    : 'rgba(255,255,255,0.9)'
                                }}
                                onClick={() => handleMotherSelection(mother.generated_id)}
                              >
                                <CardContent sx={{ p: 3 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <Checkbox
                                      checked={selectedMothers.includes(mother.generated_id)}
                                      sx={{ 
                                        color: '#f92955',
                                        '&.Mui-checked': {
                                          color: '#f92955'
                                        }
                                      }}
                                    />
                                    <Avatar sx={{ 
                                      mr: 2, 
                                      bgcolor: '#f92955',
                                      width: 50,
                                      height: 50,
                                      fontSize: '1.2rem',
                                      fontWeight: 700
                                    }}>
                                      {mother.first_name[0]}
                                    </Avatar>
                                    <Box>
                                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#333' }}>
                                        {mother.first_name} {mother.surname}
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: '#666', display: 'flex', alignItems: 'center' }}>
                                        <LocationOnIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                        {mother.location}
                                      </Typography>
                                    </Box>
                                  </Box>
                                  
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                    <Chip 
                                      icon={<ChildCareIcon />} 
                                      label={`${mother.num_children} children`} 
                                      size="small"
                                      sx={{ bgcolor: 'rgba(249, 41, 85, 0.1)', color: '#f92955' }}
                                    />
                                    <Chip 
                                      icon={<SchoolIcon />} 
                                      label={`UGX ${mother.savings?.toLocaleString() || 0}`} 
                                      size="small"
                                      sx={{ bgcolor: 'rgba(76, 175, 80, 0.1)', color: '#4caf50' }}
                                    />
                                  </Box>
                                  
                                  <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" sx={{ color: '#666', mb: 1, fontWeight: 600 }}>
                                      Funding Gap: ${calculateFundingGap(mother)}
                                    </Typography>
                                    <LinearProgress 
                                      variant="determinate" 
                                      value={Math.min(100, ((mother.savings + mother.donor_contributions) / 1200) * 100)}
                                      sx={{ 
                                        height: 8, 
                                        borderRadius: 4,
                                        bgcolor: 'rgba(249, 41, 85, 0.1)',
                                        '& .MuiLinearProgress-bar': {
                                          background: 'linear-gradient(45deg, #f92955, #d41e3f)',
                                          borderRadius: 4
                                        }
                                      }}
                                    />
                                  </Box>
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>

                        {/* Pagination */}
                        {totalPages > 1 && (
                          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                            <Pagination 
                              count={totalPages} 
                              page={page} 
                              onChange={handlePageChange}
                              color="primary"
                              sx={{
                                '& .MuiPaginationItem-root': {
                                  color: '#f92955',
                                  '&.Mui-selected': {
                                    backgroundColor: '#f92955',
                                    color: '#fff',
                                  }
                                }
                              }}
                            />
                          </Box>
                        )}

                        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
                          <Button
                            variant="outlined"
                            startIcon={<NavigateBeforeIcon />}
                            disabled={currentStep === 0}
                            onClick={handlePrevStep}
                            sx={{
                              borderColor: '#f92955',
                              color: '#f92955',
                              '&:hover': {
                                borderColor: '#d41e3f',
                                backgroundColor: 'rgba(249, 41, 85, 0.1)'
                              }
                            }}
                          >
                            Previous
                          </Button>
                          <Button
                            variant="contained"
                            endIcon={<NavigateNextIcon />}
                            disabled={selectedMothers.length === 0}
                            onClick={handleNextStep}
                            sx={{
                              background: 'linear-gradient(45deg, #f92955, #d41e3f)',
                              '&:hover': {
                                background: 'linear-gradient(45deg, #d41e3f, #b01a35)',
                              },
                              '&:disabled': {
                                background: '#ccc',
                              }
                            }}
                          >
                            Next
                          </Button>
                        </Box>
                      </Box>
                    )}

                                                             {currentStep === 1 && (
                      <Box>
                        <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#333' }}>
                          Choose Funding Type
                        </Typography>
                        
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={6}>
                            <Card 
                              sx={{ 
                                p: 3, 
                                cursor: 'pointer',
                                border: targetedFundingType === 'targeted' ? '3px solid #f92955' : '1px solid #e0e0e0',
                                background: targetedFundingType === 'targeted' ? 'rgba(249, 41, 85, 0.1)' : 'white',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  transform: 'translateY(-4px)',
                                  boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
                                }
                              }}
                              onClick={() => setTargetedFundingType('targeted')}
                            >
                              <CardContent sx={{ textAlign: 'center' }}>
                                <Typography variant="h5" sx={{ mb: 2, fontWeight: 700, color: '#333' }}>
                                  💝 Targeted Giving
                                </Typography>
                                <Typography variant="body1" sx={{ color: '#666', mb: 2 }}>
                                  General support for selected mothers
                                </Typography>
                                <Chip 
                                  label="Flexible Support" 
                                  sx={{ bgcolor: '#f92955', color: '#fff' }}
                                />
                              </CardContent>
                            </Card>
                          </Grid>
                          
                          <Grid item xs={12} md={6}>
                            <Card 
                              sx={{ 
                                p: 3, 
                                cursor: 'pointer',
                                border: targetedFundingType === 'schoolfees' ? '3px solid #BBFFC1' : '1px solid #e0e0e0',
                                background: targetedFundingType === 'schoolfees' ? 'rgba(187, 255, 193, 0.1)' : 'white',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  transform: 'translateY(-4px)',
                                  boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
                                }
                              }}
                              onClick={() => setTargetedFundingType('schoolfees')}
                            >
                              <CardContent sx={{ textAlign: 'center' }}>
                                <Typography variant="h5" sx={{ mb: 2, fontWeight: 700, color: '#333' }}>
                                  🎓 School Fees
                                </Typography>
                                <Typography variant="body1" sx={{ color: '#666', mb: 2 }}>
                                  Specific funding for education expenses
                                </Typography>
                                <Chip 
                                  label="Education Focus" 
                                  sx={{ bgcolor: '#BBFFC1', color: '#000' }}
                                />
                              </CardContent>
                            </Card>
                          </Grid>
                        </Grid>

                        {targetedFundingType === 'schoolfees' && (
                          <Box sx={{ mt: 3 }}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#333' }}>
                              School Fees Funding Type
                            </Typography>
                            <Grid container spacing={3}>
                              <Grid item xs={12} md={6}>
                                <Card 
                                  sx={{ 
                                    p: 3, 
                                    cursor: 'pointer',
                                    border: fundingType === 'onetime' ? '3px solid #BBFFC1' : '1px solid #e0e0e0',
                                    background: fundingType === 'onetime' ? 'rgba(187, 255, 193, 0.1)' : 'white',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                      transform: 'translateY(-4px)',
                                      boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
                                    }
                                  }}
                                  onClick={() => setFundingType('onetime')}
                                >
                                  <CardContent sx={{ textAlign: 'center' }}>
                                    <Typography variant="h5" sx={{ mb: 2, fontWeight: 700, color: '#333' }}>
                                      💰 One-Time Funding
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: '#666', mb: 2 }}>
                                      Fund school fees for the current term
                                    </Typography>
                                    <Chip 
                                      label="Single Payment" 
                                      sx={{ bgcolor: '#BBFFC1', color: '#000' }}
                                    />
                                  </CardContent>
                                </Card>
                              </Grid>
                              
                              <Grid item xs={12} md={6}>
                                <Card 
                                  sx={{ 
                                    p: 3, 
                                    cursor: 'pointer',
                                    border: fundingType === 'recurring' ? '3px solid #BBFFC1' : '1px solid #e0e0e0',
                                    background: fundingType === 'recurring' ? 'rgba(187, 255, 193, 0.1)' : 'white',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                      transform: 'translateY(-4px)',
                                      boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
                                    }
                                  }}
                                  onClick={() => setFundingType('recurring')}
                                >
                                  <CardContent sx={{ textAlign: 'center' }}>
                                    <Typography variant="h5" sx={{ mb: 2, fontWeight: 700, color: '#333' }}>
                                      🔄 Recurring Funding
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: '#666', mb: 2 }}>
                                      Set up automatic funding for future terms
                                    </Typography>
                                    <Chip 
                                      label="Auto-Renewal" 
                                      sx={{ bgcolor: '#BBFFC1', color: '#000' }}
                                    />
                                  </CardContent>
                                </Card>
                              </Grid>
                            </Grid>

                            {fundingType === 'recurring' && (
                              <Box sx={{ mt: 3 }}>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#333' }}>
                                  Recurring Frequency
                                </Typography>
                                <FormControl component="fieldset">
                                  <RadioGroup
                                    row
                                    value={recurringFrequency}
                                    onChange={(e) => setRecurringFrequency(e.target.value)}
                                  >
                                    <FormControlLabel
                                      value="monthly"
                                      control={<Radio sx={{ color: '#BBFFC1' }} />}
                                      label="Monthly"
                                    />
                                    <FormControlLabel
                                      value="quarterly"
                                      control={<Radio sx={{ color: '#BBFFC1' }} />}
                                      label="Quarterly"
                                    />
                                    <FormControlLabel
                                      value="yearly"
                                      control={<Radio sx={{ color: '#BBFFC1' }} />}
                                      label="Yearly"
                                    />
                                  </RadioGroup>
                                </FormControl>
                              </Box>
                            )}
                          </Box>
                        )}

                        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
                          <Button
                            variant="outlined"
                            startIcon={<NavigateBeforeIcon />}
                            onClick={handlePrevStep}
                            sx={{
                              borderColor: '#f92955',
                              color: '#f92955',
                              '&:hover': {
                                borderColor: '#d41e3f',
                                backgroundColor: 'rgba(249, 41, 85, 0.1)'
                              }
                            }}
                          >
                            Previous
                          </Button>
                          <Button
                            variant="contained"
                            onClick={handleNextStep}
                            sx={{
                              background: 'linear-gradient(45deg, #f92955, #d41e3f)',
                              '&:hover': {
                                background: 'linear-gradient(45deg, #d41e3f, #b01a35)',
                              }
                            }}
                          >
                            Next: Choose Amount
                          </Button>
                        </Box>
                      </Box>
                    )}

                     {currentStep === 2 && (
                       <Box>
                         <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#333' }}>
                           Choose Contribution Amount
                         </Typography>
                         
                         <Grid container spacing={3}>
                           <Grid item xs={12} md={6}>
                             <TextField
                               fullWidth
                               label="Amount per Mother (USD)"
                               type="number"
                               value={targetedAmount}
                               onChange={(e) => setTargetedAmount(Number(e.target.value))}
                               sx={{
                                 '& .MuiOutlinedInput-root': {
                                   borderRadius: 3,
                                   '&:hover fieldset': {
                                     borderColor: '#f92955'
                                   },
                                   '&.Mui-focused fieldset': {
                                     borderColor: '#f92955'
                                   }
                                 }
                               }}
                               InputProps={{
                                 startAdornment: <Typography sx={{ mr: 1, color: '#f92955', fontWeight: 600 }}>$</Typography>,
                               }}
                             />
                           </Grid>
                           <Grid item xs={12} md={6}>
                             <FormControl fullWidth>
                               <InputLabel>Frequency</InputLabel>
                               <Select
                                 value="monthly"
                                 label="Frequency"
                                 sx={{
                                   borderRadius: 3,
                                   '& .MuiOutlinedInput-notchedOutline': {
                                     borderColor: '#e0e0e0'
                                   },
                                   '&:hover .MuiOutlinedInput-notchedOutline': {
                                     borderColor: '#f92955'
                                   }
                                 }}
                               >
                                 <MenuItem value="monthly">Monthly</MenuItem>
                                 <MenuItem value="quarterly">Quarterly</MenuItem>
                                 <MenuItem value="one-time">One Time</MenuItem>
                               </Select>
                             </FormControl>
                           </Grid>
                         </Grid>

                         <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
                           <Button
                             variant="outlined"
                             startIcon={<NavigateBeforeIcon />}
                             onClick={handlePrevStep}
                             sx={{
                               borderColor: '#f92955',
                               color: '#f92955',
                               '&:hover': {
                                 borderColor: '#d41e3f',
                                 backgroundColor: 'rgba(249, 41, 85, 0.1)'
                               }
                             }}
                           >
                             Previous
                           </Button>
                           <Button
                             variant="contained"
                             endIcon={<NavigateNextIcon />}
                             onClick={handleNextStep}
                             sx={{
                               background: 'linear-gradient(45deg, #f92955, #d41e3f)',
                               '&:hover': {
                                 background: 'linear-gradient(45deg, #d41e3f, #b01a35)',
                               }
                             }}
                           >
                             Next
                           </Button>
                         </Box>
                       </Box>
                     )}

                     {currentStep === 3 && (
                       <Box>
                         <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#333' }}>
                           Choose Payment Method
                         </Typography>
                         
                         {paymentMethods.length === 0 ? (
                           <Box sx={{ 
                             textAlign: 'center', 
                             py: 6,
                             color: '#666'
                           }}>
                             <Typography variant="h6" sx={{ color: '#999', mb: 1 }}>
                               No Payment Methods Available
                             </Typography>
                             <Typography variant="body2" sx={{ color: '#999', mb: 3 }}>
                               Please add payment methods in Settings first
                             </Typography>
                             <Button
                               variant="outlined"
                               onClick={() => window.location.href = `/partner/${partnerId}/settings`}
                               sx={{
                                 borderColor: '#f92955',
                                 color: '#f92955',
                                 '&:hover': {
                                   borderColor: '#d41e3f',
                                   bgcolor: '#f9295515',
                                 },
                               }}
                             >
                               Go to Settings
                             </Button>
                           </Box>
                         ) : (
                           <Grid container spacing={2}>
                             {paymentMethods.map((method) => (
                               <Grid item xs={12} sm={6} md={4} key={method.id}>
                                 <Card 
                                   sx={{ 
                                     cursor: 'pointer',
                                     border: selectedPaymentMethod === method.id ? '3px solid #f92955' : '1px solid #e0e0e0',
                                     borderRadius: 3,
                                     transition: 'all 0.3s ease',
                                     '&:hover': { 
                                       transform: 'translateY(-4px)',
                                       boxShadow: '0 12px 30px rgba(0,0,0,0.15)'
                                     },
                                     background: selectedPaymentMethod === method.id 
                                       ? 'linear-gradient(135deg, rgba(249, 41, 85, 0.1) 0%, rgba(212, 30, 63, 0.1) 100%)'
                                       : 'rgba(255,255,255,0.9)'
                                   }}
                                   onClick={() => setSelectedPaymentMethod(method.id)}
                                 >
                                   <CardContent sx={{ textAlign: 'center', p: 3 }}>
                                     <Typography variant="h3" sx={{ mb: 2 }}>
                                       {getPaymentIcon(method.type, method.provider)}
                                     </Typography>
                                     <Typography variant="body1" sx={{ fontWeight: 600, color: '#333', mb: 1 }}>
                                       {getPaymentDisplayName(method.type, method.provider)}
                                     </Typography>
                                     <Typography variant="body2" sx={{ color: '#666', fontSize: '0.8rem' }}>
                                       {method.name}
                                     </Typography>
                                     <Typography variant="body2" sx={{ color: '#999', fontSize: '0.7rem', mt: 1 }}>
                                       {method.type === 'card' 
                                         ? maskCardNumber(method.number)
                                         : maskPhoneNumber(method.number)
                                       }
                                     </Typography>
                                   </CardContent>
                                 </Card>
                               </Grid>
                             ))}
                           </Grid>
                         )}

                         <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
                           <Button
                             variant="outlined"
                             startIcon={<NavigateBeforeIcon />}
                             onClick={handlePrevStep}
                             sx={{
                               borderColor: '#f92955',
                               color: '#f92955',
                               '&:hover': {
                                 borderColor: '#d41e3f',
                                 backgroundColor: 'rgba(249, 41, 85, 0.1)'
                               }
                             }}
                           >
                             Previous
                           </Button>
                           <Button
                             variant="contained"
                             endIcon={<NavigateNextIcon />}
                             disabled={!selectedPaymentMethod}
                             onClick={handleNextStep}
                             sx={{
                               background: 'linear-gradient(45deg, #f92955, #d41e3f)',
                               '&:hover': {
                                 background: 'linear-gradient(45deg, #d41e3f, #b01a35)',
                               },
                               '&:disabled': {
                                 background: '#ccc',
                               }
                             }}
                           >
                             Next
                           </Button>
                         </Box>
                       </Box>
                     )}

                                         {currentStep === 3 && (
                       <Box>
                         <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#333' }}>
                           Review & Confirm
                         </Typography>
                         
                         <Grid container spacing={3}>
                           <Grid item xs={12} md={8}>
                             <Card sx={{ 
                               background: 'linear-gradient(135deg, rgba(249, 41, 85, 0.05) 0%, rgba(212, 30, 63, 0.05) 100%)',
                               border: '1px solid rgba(249, 41, 85, 0.2)',
                               borderRadius: 3
                             }}>
                               <CardContent>
                                 <Typography variant="h6" sx={{ mb: 2, color: '#333', fontWeight: 700 }}>
                                   Selected Mothers ({selectedMothers.length})
                                 </Typography>
                                 <Grid container spacing={1}>
                                   {mothers.filter(m => selectedMothers.includes(m.generated_id)).map((mother) => (
                                     <Grid item xs={12} sm={6} key={mother.generated_id}>
                                       <Box sx={{ 
                                         display: 'flex', 
                                         alignItems: 'center', 
                                         p: 1,
                                         borderRadius: 2,
                                         bgcolor: 'rgba(255,255,255,0.7)'
                                       }}>
                                         <Avatar sx={{ 
                                           mr: 2, 
                                           bgcolor: '#f92955',
                                           width: 40,
                                           height: 40,
                                           fontSize: '1rem'
                                         }}>
                                           {mother.first_name[0]}
                                         </Avatar>
                                         <Box>
                                           <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                                             {mother.first_name} {mother.surname}
                                           </Typography>
                                           <Typography variant="caption" sx={{ color: '#666' }}>
                                             Gap: ${calculateFundingGap(mother)}
                                           </Typography>
                                         </Box>
                                       </Box>
                                     </Grid>
                                   ))}
                                 </Grid>
                                 
                                 <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid rgba(249, 41, 85, 0.2)' }}>
                                   <Typography variant="h6" sx={{ mb: 2, color: '#333', fontWeight: 700 }}>
                                     Contribution Details
                                   </Typography>
                                   <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                     <Typography variant="body2" sx={{ color: '#666' }}>
                                       Amount per mother:
                                     </Typography>
                                     <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                                       ${targetedAmount}
                                     </Typography>
                                   </Box>
                                   <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                     <Typography variant="body2" sx={{ color: '#666' }}>
                                       Frequency:
                                     </Typography>
                                     <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                                       Monthly
                                     </Typography>
                                   </Box>
                                   <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                     <Typography variant="body2" sx={{ color: '#666' }}>
                                       Payment Method:
                                     </Typography>
                                     <Box sx={{ textAlign: 'right' }}>
                                       <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                                         {(() => {
                                           const method = paymentMethods.find(m => m.id === selectedPaymentMethod);
                                           return method ? getPaymentDisplayName(method.type, method.provider) : 'Not selected';
                                         })()}
                                       </Typography>
                                       {(() => {
                                         const method = paymentMethods.find(m => m.id === selectedPaymentMethod);
                                         return method && (
                                           <Typography variant="body2" sx={{ color: '#999', fontSize: '0.7rem' }}>
                                             {method.name} • {method.type === 'card' 
                                               ? maskCardNumber(method.number)
                                               : maskPhoneNumber(method.number)
                                             }
                                           </Typography>
                                         );
                                       })()}
                                     </Box>
                                   </Box>
                                 </Box>
                               </CardContent>
                             </Card>
                           </Grid>
                           
                           <Grid item xs={12} md={4}>
                             <Card sx={{ 
                               background: 'linear-gradient(135deg, #BBFFC1 0%, #a8ffae 100%)',
                               color: '#000',
                               borderRadius: 4,
                               boxShadow: '0 15px 35px rgba(187, 255, 193, 0.3)',
                               transition: 'all 0.3s ease',
                               '&:hover': {
                                 transform: 'translateY(-8px)',
                                 boxShadow: '0 20px 40px rgba(187, 255, 193, 0.4)',
                               }
                             }}>
                               <CardContent sx={{ textAlign: 'center', py: 4 }}>
                                 <Typography variant="h2" sx={{ 
                                   fontWeight: 900, 
                                   mb: 2,
                                   fontSize: '3.5rem',
                                   textShadow: '0 4px 8px rgba(0,0,0,0.2)'
                                 }}>
                                   ${targetedAmount * selectedMothers.length}
                                 </Typography>
                                 <Typography variant="h6" sx={{ 
                                   opacity: 0.9, 
                                   mb: 2,
                                   fontWeight: 600
                                 }}>
                                   Total Contribution
                                 </Typography>
                                 <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                   ${targetedAmount} per mother
                                 </Typography>
                               </CardContent>
                             </Card>
                           </Grid>
                         </Grid>

                         <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
                           <Button
                             variant="outlined"
                             startIcon={<NavigateBeforeIcon />}
                             onClick={handlePrevStep}
                             sx={{
                               borderColor: '#f92955',
                               color: '#f92955',
                               '&:hover': {
                                 borderColor: '#d41e3f',
                                 backgroundColor: 'rgba(249, 41, 85, 0.1)'
                               }
                             }}
                           >
                             Previous
                           </Button>
                           <Button
                             variant="contained"
                             startIcon={<EmojiEventsIcon />}
                             onClick={() => {
                               // Handle final submission
                               alert('Targeted contribution submitted successfully!');
                               resetToStep1();
                             }}
                             sx={{
                               background: 'linear-gradient(45deg, #f92955, #d41e3f)',
                               '&:hover': {
                                 background: 'linear-gradient(45deg, #d41e3f, #b01a35)',
                               }
                             }}
                           >
                             Confirm & Submit
                           </Button>
                         </Box>
                       </Box>
                     )}
                  </CardContent>
                </Card>
              </Grow>
            )}



          </Box>
        </Slide>
      </Box>
    </Box>
  );
};

export default MatchFunding;
